import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, Loader2 } from 'lucide-react';

function colorForCount(count, max) {
  if (count === 0 || max === 0) return 'bg-slate-100';
  const ratio = count / max;
  if (ratio > 0.75) return 'bg-emerald-700';
  if (ratio > 0.5) return 'bg-emerald-600';
  if (ratio > 0.25) return 'bg-emerald-400';
  if (ratio > 0.1) return 'bg-emerald-300';
  return 'bg-emerald-200';
}

export default function ActivityHeatmap() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [userId, setUserId] = useState('all');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/users');
        setUsers((res.data || []).filter(u => u.id));
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ days });
        if (userId !== 'all') params.set('user_id', userId);
        const res = await api.get(`/activity/heatmap?${params.toString()}`);
        setData(res.data);
      } catch { /* silent */ }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, userId]);

  return (
    <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-sky-400" data-testid="activity-heatmap">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Calendar size={16} className="text-sky-600" /> Activity Heat-Map
        </CardTitle>
        <p className="text-xs text-muted-foreground">When are users most active? Day of week × hour of day. Useful for spotting late-night unusual access.</p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="heatmap-user-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id} className="text-xs">{u.full_name || u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="animate-spin text-sky-600" size={22} />
          </div>
        ) : data && (
          <>
            <div className="text-[10px] text-slate-500 mb-2">
              {data.total_events} total requests · Max in a single hour: {data.max_in_cell}
            </div>
            <div className="overflow-x-auto">
              <table className="border-separate" style={{ borderSpacing: 2 }}>
                <thead>
                  <tr>
                    <th className="w-10"></th>
                    {data.hour_labels.map(h => (
                      <th key={h} className="text-[9px] text-slate-400 font-normal w-5 text-center">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.day_labels.map((label, dayIdx) => (
                    <tr key={label}>
                      <td className="text-[10px] text-slate-500 font-medium pr-2">{label}</td>
                      {data.grid[dayIdx].map((count, hourIdx) => (
                        <td key={`${label}-${hourIdx}`} className="p-0">
                          <div
                            className={`w-5 h-5 rounded ${colorForCount(count, data.max_in_cell)} hover:ring-2 hover:ring-sky-400 cursor-pointer transition-all`}
                            title={`${label} ${hourIdx.toString().padStart(2, '0')}:00 — ${count} request${count !== 1 ? 's' : ''}`}
                            data-testid={`heatmap-cell-${dayIdx}-${hourIdx}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2 mt-3 text-[10px] text-slate-500">
              <span>Less</span>
              <div className="w-4 h-4 bg-slate-100 rounded" />
              <div className="w-4 h-4 bg-emerald-200 rounded" />
              <div className="w-4 h-4 bg-emerald-300 rounded" />
              <div className="w-4 h-4 bg-emerald-400 rounded" />
              <div className="w-4 h-4 bg-emerald-600 rounded" />
              <div className="w-4 h-4 bg-emerald-700 rounded" />
              <span>More</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
